import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Plus, Search, ChevronUp, ChevronDown, Users, ChevronLeft, ChevronRight, Building2, UserCheck, Shield, Crown } from 'lucide-react'
import { PageHeader, fadeUp } from '@personic/shared'
import { useAuthStore } from '@personic/shared'
import { buildBreadcrumbs } from '@/config/routes'
import { useAdminUsers, useUserDirectorySummary, type AdminUser } from '@/hooks/useAdminUsers'
import CreateUserModal from '@/components/admin/CreateUserModal'
import { formatDistanceToNow } from 'date-fns'

// =============================================================================
// ROLE BADGE CONFIG
// =============================================================================

const ROLE_CONFIG: Record<string, { label: string; bg: string; text: string; dot: string }> = {
  superuser:    { label: 'Superuser',    bg: 'bg-amber-50',   text: 'text-amber-700',  dot: 'bg-amber-500' },
  admin:        { label: 'Admin',        bg: 'bg-purple-50',  text: 'text-purple-700', dot: 'bg-purple-500' },
  employee:     { label: 'Employee',     bg: 'bg-sky-50',     text: 'text-sky-700',    dot: 'bg-sky-500' },
  tenant_admin: { label: 'Tenant Admin', bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  consumer:     { label: 'Consumer',     bg: 'bg-teal-50',    text: 'text-teal-700',   dot: 'bg-teal-500' },
}

function RoleBadge({ role }: { role: string }) {
  const config = ROLE_CONFIG[role] || { label: role, bg: 'bg-warm-50', text: 'text-warm-700', dot: 'bg-warm-500' }
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
      {config.label}
    </span>
  )
}

function StatusBadge({ isActive }: { isActive: boolean }) {
  return isActive ? (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700">
      <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
      Active
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-warm-100 text-warm-500">
      <span className="w-1.5 h-1.5 rounded-full bg-warm-400" />
      Inactive
    </span>
  )
}

// =============================================================================
// STAT CARD
// =============================================================================

const STAT_COLORS: Record<string, { bg: string; icon: string }> = {
  copper:  { bg: 'bg-copper-50',  icon: 'text-copper-500' },
  sky:     { bg: 'bg-sky-50',     icon: 'text-sky-500' },
  emerald: { bg: 'bg-emerald-50', icon: 'text-emerald-500' },
  purple:  { bg: 'bg-purple-50',  icon: 'text-purple-500' },
  amber:   { bg: 'bg-amber-50',   icon: 'text-amber-500' },
  teal:    { bg: 'bg-teal-50',    icon: 'text-teal-500' },
}

function StatCard({ label, value, icon: Icon, color = 'copper' }: {
  label: string
  value: number | string
  icon: React.ElementType
  color?: string
}) {
  const c = STAT_COLORS[color] || STAT_COLORS.copper
  return (
    <div className="flex items-center gap-3 p-4 rounded-xl border border-warm-200 bg-white">
      <div className={`p-2 rounded-lg ${c.bg}`}>
        <Icon className={`w-4 h-4 ${c.icon}`} />
      </div>
      <div>
        <p className="text-lg font-semibold text-midnight-800">{value}</p>
        <p className="text-xs text-warm-500">{label}</p>
      </div>
    </div>
  )
}

// =============================================================================
// FILTER OPTIONS
// =============================================================================

const ROLE_OPTIONS = [
  { value: '', label: 'All Roles' },
  { value: 'superuser', label: 'Superuser' },
  { value: 'admin', label: 'Admin' },
  { value: 'employee', label: 'Employee' },
  { value: 'tenant_admin', label: 'Tenant Admin' },
  { value: 'consumer', label: 'Consumer' },
]

const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
]

type SortField = 'name' | 'email' | 'role' | 'created_at' | 'last_login'
type SortDir = 'asc' | 'desc'

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function UserDirectory() {
  const navigate = useNavigate()
  const { user: currentUser } = useAuthStore()
  const breadcrumbs = buildBreadcrumbs('/admin/users')

  // Filters
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [page, setPage] = useState(0)
  const [sortField, setSortField] = useState<SortField>('created_at')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [showCreateModal, setShowCreateModal] = useState(false)

  const limit = 25

  const { data: summary } = useUserDirectorySummary()

  const { data, isLoading } = useAdminUsers({
    search: search || undefined,
    role: roleFilter || undefined,
    is_active: statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined,
    limit: 200, // Fetch more for client-side sort
    offset: 0,
  })

  const canCreateUsers = currentUser?.role === 'superuser' || currentUser?.role === 'admin'

  // Client-side sort
  const sortedUsers = useMemo(() => {
    if (!data?.users) return []
    const users = [...data.users]
    users.sort((a, b) => {
      let cmp = 0
      switch (sortField) {
        case 'name':
          cmp = a.name.localeCompare(b.name)
          break
        case 'email':
          cmp = a.email.localeCompare(b.email)
          break
        case 'role':
          cmp = a.role.localeCompare(b.role)
          break
        case 'created_at':
          cmp = (a.created_at || '').localeCompare(b.created_at || '')
          break
        case 'last_login':
          cmp = (a.last_login || '').localeCompare(b.last_login || '')
          break
      }
      return sortDir === 'asc' ? cmp : -cmp
    })
    return users
  }, [data?.users, sortField, sortDir])

  const paginatedUsers = sortedUsers.slice(page * limit, (page + 1) * limit)
  const totalPages = Math.ceil(sortedUsers.length / limit)

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDir('asc')
    }
    setPage(0)
  }

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronUp className="w-3 h-3 text-warm-300" />
    return sortDir === 'asc'
      ? <ChevronUp className="w-3 h-3 text-copper-500" />
      : <ChevronDown className="w-3 h-3 text-copper-500" />
  }

  function getCompanyName(user: AdminUser) {
    if (user.role === 'consumer') return 'Identity'
    if (user.tenant_name) return user.tenant_name
    if (['superuser', 'admin', 'employee'].includes(user.role)) return 'Personic AI'
    return '\u2014'
  }

  return (
    <div>
      <PageHeader
        title="User Directory"
        description="Unified view of all platform users across roles and tenants."
        breadcrumbs={breadcrumbs}
        action={
          canCreateUsers ? (
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn btn-primary inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Create User
            </button>
          ) : undefined
        }
      />

      {/* Summary Cards */}
      {summary && (
        <motion.div variants={fadeUp} initial="hidden" animate="visible" className="grid grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
          <StatCard label="Total Users" value={summary.total_users.toLocaleString()} icon={Users} color="copper" />
          <StatCard label="Tenants" value={summary.total_tenants.toLocaleString()} icon={Building2} color="sky" />
          <StatCard label="Identity Users" value={summary.consumers.toLocaleString()} icon={UserCheck} color="teal" />
          <StatCard label="Employees" value={summary.employees.toLocaleString()} icon={Users} color="emerald" />
          <StatCard label="Admins" value={summary.admins.toLocaleString()} icon={Shield} color="purple" />
          <StatCard label="Superusers" value={summary.superusers.toLocaleString()} icon={Crown} color="amber" />
        </motion.div>
      )}

      <motion.div variants={fadeUp} initial="hidden" animate="visible">
        {/* Filter Bar */}
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-400" />
            <input
              type="text"
              placeholder="Search by name, email, or company..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(0) }}
              className="input pl-10 w-full"
            />
          </div>
          <select
            value={roleFilter}
            onChange={e => { setRoleFilter(e.target.value); setPage(0) }}
            className="input w-auto min-w-[140px]"
          >
            {ROLE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setPage(0) }}
            className="input w-auto min-w-[130px]"
          >
            {STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <div className="ml-auto flex items-center gap-2 text-sm text-warm-500">
            <Users className="w-4 h-4" />
            <span>{sortedUsers.length} user{sortedUsers.length !== 1 ? 's' : ''}</span>
          </div>
        </div>

        {/* Table */}
        <div className="card overflow-hidden">
          <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-380px)]">
            <table className="min-w-full divide-y divide-warm-200">
              <thead className="bg-warm-50/60 sticky top-0 z-10">
                <tr>
                  {[
                    { field: 'name' as SortField, label: 'Name' },
                    { field: 'email' as SortField, label: 'Email' },
                    { field: 'role' as SortField, label: 'Type' },
                    { field: null, label: 'Company' },
                    { field: 'created_at' as SortField, label: 'Date Added' },
                    { field: 'last_login' as SortField, label: 'Last Login' },
                    { field: null, label: 'Status' },
                  ].map(col => (
                    <th
                      key={col.label}
                      className={`px-5 py-3.5 text-left text-xs font-semibold text-warm-500 uppercase tracking-wider ${
                        col.field ? 'cursor-pointer select-none hover:text-midnight-700' : ''
                      }`}
                      onClick={col.field ? () => toggleSort(col.field!) : undefined}
                    >
                      <span className="inline-flex items-center gap-1">
                        {col.label}
                        {col.field && <SortIcon field={col.field} />}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-warm-100 bg-white">
                {isLoading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 7 }).map((_, j) => (
                        <td key={j} className="px-5 py-4">
                          <div className="h-4 bg-warm-100 rounded animate-pulse" style={{ width: `${50 + Math.random() * 40}%` }} />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : paginatedUsers.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-5 py-16 text-center">
                      <Users className="w-10 h-10 text-warm-300 mx-auto mb-3" />
                      <p className="text-warm-500 font-medium">No users found</p>
                      <p className="text-warm-400 text-sm mt-1">Try adjusting your search or filters.</p>
                    </td>
                  </tr>
                ) : (
                  paginatedUsers.map(u => (
                    <tr
                      key={u.id}
                      onClick={() => navigate(`/admin/users/${u.id}`)}
                      className="cursor-pointer hover:bg-copper-50/30 transition-colors"
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-copper-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-copper-600 font-semibold text-xs">
                              {u.name?.charAt(0).toUpperCase() || '?'}
                            </span>
                          </div>
                          <span className="text-sm font-medium text-midnight-800 truncate max-w-[180px]">
                            {u.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-sm text-warm-600 truncate max-w-[200px]">
                        {u.email}
                      </td>
                      <td className="px-5 py-4">
                        <RoleBadge role={u.role} />
                      </td>
                      <td className="px-5 py-4 text-sm text-warm-600 truncate max-w-[160px]">
                        {getCompanyName(u)}
                      </td>
                      <td className="px-5 py-4 text-sm text-warm-500">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '\u2014'}
                      </td>
                      <td className="px-5 py-4 text-sm text-warm-500">
                        {u.last_login ? formatDistanceToNow(new Date(u.last_login), { addSuffix: true }) : 'Never'}
                      </td>
                      <td className="px-5 py-4">
                        <StatusBadge isActive={u.is_active} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-warm-200 bg-warm-50/40">
              <p className="text-sm text-warm-500">
                Showing {page * limit + 1}–{Math.min((page + 1) * limit, sortedUsers.length)} of {sortedUsers.length}
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="p-1.5 rounded-lg hover:bg-warm-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4 text-warm-600" />
                </button>
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i)}
                    className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                      page === i
                        ? 'bg-copper-500 text-white'
                        : 'text-warm-600 hover:bg-warm-100'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page === totalPages - 1}
                  className="p-1.5 rounded-lg hover:bg-warm-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-4 h-4 text-warm-600" />
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Create User Modal */}
      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={(userId) => {
            setShowCreateModal(false)
            navigate(`/admin/users/${userId}`)
          }}
        />
      )}
    </div>
  )
}
